/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  isSupabaseConfigured,
  legacySupabase,
  primarySupabase,
  supabase,
} from './supabase';
import { clearQuickPinUnlock } from './quickPinApi';

const AuthContext = createContext(null);

function fallbackProfile(user) {
  if (!user) return null;
  return {
    id: user.id,
    email: user.email,
    full_name: user.user_metadata?.full_name ?? '',
    role: 'student',
  };
}

async function readFunctionError(error, fallback) {
  let message = error?.message || fallback;
  try {
    const details = await error?.context?.json();
    message = details?.error || message;
  } catch {
    // Keep the original function error.
  }
  return message;
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(isSupabaseConfigured);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return undefined;
    }

    let active = true;
    let profileRequest = 0;

    async function loadProfile(user) {
      const requestNumber = ++profileRequest;

      if (!user) {
        if (active && requestNumber === profileRequest) setProfile(null);
        return;
      }

      const now = new Date().toISOString();
      const { error: upsertError } = await supabase
        .from('profiles')
        .upsert(
          { id: user.id, email: user.email, last_seen_at: now },
          { onConflict: 'id' },
        );

      if (upsertError) {
        console.warn('Profile activity update failed:', upsertError.message);
      }

      const { data, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (!active || requestNumber !== profileRequest) return;

      if (profileError) {
        console.error('Profile load failed:', profileError.message);
        setAuthError('Your account is signed in, but the profile record could not be loaded.');
        setProfile(fallbackProfile(user));
        return;
      }

      setAuthError('');
      setProfile(data ?? fallbackProfile(user));
    }

    async function initializeAuth() {
      try {
        const { data, error } = await primarySupabase.auth.getSession();
        if (error) throw error;
        if (!active) return;

        const initialSession = data.session ?? null;
        setSession(initialSession);
        await loadProfile(initialSession?.user ?? null);
      } catch (error) {
        console.error('Authentication initialization failed:', error);
        if (active) {
          setSession(null);
          setProfile(null);
          setAuthError(error?.message || 'The secure portal could not initialize.');
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    initializeAuth();

    const { data: listener } = primarySupabase.auth.onAuthStateChange((event, nextSession) => {
      if (!active || event === 'INITIAL_SESSION') return;

      setSession(nextSession ?? null);

      if (event === 'SIGNED_OUT' || !nextSession?.user) {
        profileRequest += 1;
        setProfile(null);
        setAuthError('');
        setLoading(false);
        return;
      }

      if (event === 'TOKEN_REFRESHED') return;

      setLoading(true);

      window.setTimeout(() => {
        if (!active) return;
        loadProfile(nextSession.user)
          .catch((error) => {
            console.error('Profile refresh failed:', error);
            if (active) {
              setProfile(fallbackProfile(nextSession.user));
              setAuthError(error?.message || 'The account profile could not be refreshed.');
            }
          })
          .finally(() => {
            if (active) setLoading(false);
          });
      }, 0);
    });

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      loading,
      authError,
      configured: isSupabaseConfigured,
      isAdmin: profile?.role === 'admin',
      signInWithMagicLink: async (email) => {
        if (!primarySupabase) throw new Error('Supabase is not configured.');
        const normalizedEmail = String(email ?? '').trim().toLowerCase();
        return primarySupabase.auth.signInWithOtp({
          email: normalizedEmail,
          options: {
            shouldCreateUser: false,
            emailRedirectTo: `${window.location.origin}/portal`,
          },
        });
      },
      signIn: async (email, password) => {
        if (!primarySupabase) throw new Error('Supabase is not configured.');

        const normalizedEmail = String(email ?? '').trim().toLowerCase();
        const firstAttempt = await primarySupabase.auth.signInWithPassword({
          email: normalizedEmail,
          password,
        });

        if (!firstAttempt.error) return firstAttempt;

        // Existing Jonny's Towing clients can keep their historical credentials.
        // The server validates them against the old project, synchronizes the
        // recovered account, then this browser signs into the recovered project.
        const { data: bridgeData, error: bridgeError } = await primarySupabase.functions.invoke(
          'legacy-bridge-login',
          { body: { email: normalizedEmail, password } },
        );

        if (bridgeError || !bridgeData?.success) {
          const message = bridgeError
            ? await readFunctionError(bridgeError, 'The email or password is incorrect.')
            : bridgeData?.error || 'The email or password is incorrect.';
          return { data: { user: null, session: null }, error: new Error(message) };
        }

        return primarySupabase.auth.signInWithPassword({
          email: normalizedEmail,
          password,
        });
      },
      signUp: async (email, password, fullName = '') => {
        if (!primarySupabase) throw new Error('Supabase is not configured.');
        return primarySupabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/portal`,
            data: { full_name: fullName },
          },
        });
      },
      signOut: async () => {
        clearQuickPinUnlock(session?.user?.id);
        await Promise.allSettled([
          primarySupabase?.auth.signOut(),
          legacySupabase?.auth.signOut(),
        ]);
      },
      resetPassword: async (email) => {
        if (!primarySupabase) throw new Error('Supabase is not configured.');
        return primarySupabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/portal/login`,
        });
      },
    }),
    [session, profile, loading, authError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
